import { queryClient } from "@/query-client";
import { queryOptions, useMutation } from "@tanstack/react-query";
import type { CreateLeadData, UpdateLeadData } from "../../types/lead";
import leadService from "../services/lead-service";

export const leadDashboardQueryOptions = (userId?: string) =>
	queryOptions({
		queryKey: ["leads", "dashboard", userId],
		queryFn: () => leadService.dashboard(userId),
	});

export const leadsQueryOptions = (params: Record<string, unknown> = {}) =>
	queryOptions({
		queryKey: ["leads", params],
		queryFn: () => leadService.list(params),
	});

export const leadQueryOptions = (id: string) =>
	queryOptions({
		queryKey: ["lead", id],
		queryFn: () => leadService.findById(id),
	});

export const leadActivitiesQueryOptions = (id: string) =>
	queryOptions({
		queryKey: ["lead", id, "activities"],
		queryFn: () => leadService.activities(id),
	});

export const leadCategoriesQueryOptions = () =>
	queryOptions({
		queryKey: ["leads", "categories"],
		queryFn: () => leadService.categories(),
	});

export const leadCategoryListQueryOptions = () =>
	queryOptions({
		queryKey: ["lead-categories"],
		queryFn: () => leadService.leadCategories(),
		staleTime: 1000 * 60 * 5,
	});

export const useLeadCreateMutation = () =>
	useMutation({
		mutationFn: (lead: CreateLeadData) => leadService.create(lead),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["leads"] });
		},
	});

export const useLeadUpdateMutation = (id: string) =>
	useMutation({
		mutationFn: (lead: UpdateLeadData) => leadService.updateById(id, lead),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["leads"] });
			queryClient.invalidateQueries({ queryKey: ["lead", id] });
		},
	});

export const useLeadDeleteMutation = () =>
	useMutation({
		mutationFn: (id: string) => leadService.deleteById(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["leads"] });
		},
	});

export const useLeadImportMutation = () =>
	useMutation({
		mutationFn: ({ file, category }: { file: File; category: string }) => leadService.importExcel(file, category),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["leads"] });
		},
	});

export const useLeadImportBatchMutation = () =>
	useMutation({
		mutationFn: (leads: Record<string, unknown>[]) => leadService.importBatch(leads),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["leads"] });
		},
	});

export const leadNotesQueryOptions = (id: string) =>
	queryOptions({
		queryKey: ["lead", id, "notes"],
		queryFn: () => leadService.notes(id),
	});

export const useLeadNoteCreateMutation = (leadId: string) =>
	useMutation({
		mutationFn: (formData: FormData) => leadService.createNote(leadId, formData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["lead", leadId, "notes"] });
		},
	});

export const useLeadNoteDeleteMutation = (leadId: string) =>
	useMutation({
		mutationFn: (noteId: string) => leadService.deleteNote(leadId, noteId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["lead", leadId, "notes"] });
		},
	});

export const leadStatusesQueryOptions = () =>
	queryOptions({
		queryKey: ["lead-statuses"],
		queryFn: () => leadService.statuses(),
		staleTime: 1000 * 60 * 5,
	});

export const useLeadStatusCreateMutation = () =>
	useMutation({
		mutationFn: (data: { name: string; color: string; sort_order?: number; is_default?: boolean }) => leadService.createStatus(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["lead-statuses"] });
		},
	});

export const useLeadStatusUpdateMutation = () =>
	useMutation({
		mutationFn: ({ id, ...data }: { id: string; name?: string; color?: string; sort_order?: number; is_default?: boolean }) =>
			leadService.updateStatus(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["lead-statuses"] });
		},
	});

export const useLeadStatusDeleteMutation = () =>
	useMutation({
		mutationFn: (id: string) => leadService.deleteStatus(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["lead-statuses"] });
		},
	});
